import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Search, Download, X, Send, FileText, Printer } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});
const API = apiBaseUrl;

const fmt  = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;
const fmtR = (n) => `Rs. ${parseFloat(n||0).toLocaleString("en-IN", {minimumFractionDigits:1, maximumFractionDigits:1})}`;

// ── Number to words ───────────────────────────────────────────────
const numToWords = (n) => {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
    'Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const w = (n) => {
    if (n === 0) return '';
    if (n < 20)  return ones[n];
    if (n < 100) return tens[Math.floor(n/10)] + (n%10 ? ' '+ones[n%10] : '');
    if (n < 1000) return ones[Math.floor(n/100)] + ' Hundred' + (n%100?' '+w(n%100):'');
    if (n < 100000) return w(Math.floor(n/1000)) + ' Thousand' + (n%1000?' '+w(n%1000):'');
    if (n < 10000000) return w(Math.floor(n/100000)) + ' Lakh' + (n%100000?' '+w(n%100000):'');
    return w(Math.floor(n/10000000)) + ' Crore' + (n%10000000?' '+w(n%10000000):'');
  };
  const amount = Math.round(n);
  return (w(amount) || 'Zero') + ' Rupees';
};

// ── Download PDF from backend ─────────────────────────────────────
const downloadPayslipPdf = async (id, empCode, month, setLoading) => {
  if (setLoading) setLoading(true);
  // Send company info as POST body — supports base64 logos (too large for URL params)
  const cname = sessionStorage.getItem("company_name")    || localStorage.getItem("company_name")    || "";
  const caddr = sessionStorage.getItem("company_address") || localStorage.getItem("company_address") || "";
  const clogo = sessionStorage.getItem("company_logo")    || localStorage.getItem("company_logo")    || "";
  try {
    const res = await axios.post(`${API}/payslips/${id}/pdf`,
      { company_name: cname, company_address: caddr, logo_data: clogo },
      { ...ax(), responseType: "blob" },
    );
    const url  = URL.createObjectURL(new Blob([res.data], { type:"application/pdf" }));
    const link = document.createElement("a");
    link.href     = url;
    link.download = `payslip_${empCode || id}_${month}.pdf`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  } catch(e) { alert("PDF download failed."); }
  finally { if (setLoading) setLoading(false); }
};

// ── Pagination bar ───────────────────────────────────────────────
// Numbered pagination with ellipsis for large page counts.
// Works off whatever list is passed in (already filtered/searched) —
// caller controls the slicing.
function PaginationBar({ page, totalPages, onChange, totalItems, pageSize, T }) {
  if (totalPages <= 1) return null;

  const delta = 2;
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, totalItems);

  const btnBase = {
    minWidth:30, height:30, padding:"0 8px", borderRadius:7,
    border:`1px solid ${T.border}`, background:"none", color:T.muted,
    cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600,
  };

  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"14px 22px", borderTop:`1px solid ${T.border}`, flexWrap:"wrap", gap:10 }}>
      <div style={{ fontSize:12, color:T.muted }}>
        Showing <strong style={{ color:T.text }}>{start}</strong>–<strong style={{ color:T.text }}>{end}</strong> of <strong style={{ color:T.text }}>{totalItems}</strong>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <button disabled={page===1} onClick={() => onChange(page - 1)}
          style={{ ...btnBase, opacity: page===1 ? 0.4 : 1, cursor: page===1 ? "not-allowed" : "pointer" }}>
          ‹
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} style={{ color:T.dim, fontSize:12, padding:"0 4px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onChange(p)}
            style={{ ...btnBase,
              background: p===page ? T.cyan : "none",
              borderColor: p===page ? T.cyan : T.border,
              color: p===page ? "#fff" : T.muted,
              fontWeight: p===page ? 800 : 600 }}>
            {p}
          </button>
        ))}
        <button disabled={page===totalPages} onClick={() => onChange(page + 1)}
          style={{ ...btnBase, opacity: page===totalPages ? 0.4 : 1, cursor: page===totalPages ? "not-allowed" : "pointer" }}>
          ›
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// PAYSLIP MODAL — N7VEN format (matching PDF exactly)
// NOTE: The payslip document body below intentionally mirrors the printed
// PDF (fixed white/navy palette) regardless of admin theme — only the
// surrounding action bar follows the app theme.
// ══════════════════════════════════════════════════════════════════
function PayslipModal({ ps, onClose }) {
  const { T } = useTheme();
  const companyLogo  = sessionStorage.getItem("company_logo") ||
                       localStorage.getItem("company_logo")   || null;
  const companyName  = sessionStorage.getItem("company_name") ||
                       localStorage.getItem("company_name")   || "Hirality";
  const companyAddr  = sessionStorage.getItem("company_address") || "";

  const comps = (() => {
    try { return JSON.parse(typeof ps.components === "string" ? ps.components : JSON.stringify(ps.components||{})); }
    catch { return {}; }
  })();

  const earnings   = comps.earnings   || {};
  const deductions = comps.deductions || {};
  const totalDays  = ps.working_days  || 26;
  const daysPaid   = totalDays - (ps.lop_days || 0);

  const [downloading, setDownloading] = useState(false);
  const [sending,     setSending]     = useState(false);
  const [sent,        setSent]        = useState(false);

  const handleSend = async () => {
    if (!window.confirm(`Send payslip to ${ps.employee_name}?`)) return;
    setSending(true);
    try {
      await axios.post(`${API}/payslips/${ps.id}/send-email`, {}, ax());
      setSent(true);
      alert(`✅ Payslip sent to employee's email`);
    } catch(e) {
      alert("❌ " + (e.response?.data?.detail || "Send failed"));
    } finally { setSending(false); }
  };

  // ── Table row helpers (fixed printable-document palette) ──────
  const InfoRow = ({ label, value, alt }) => (
    <tr>
      <td style={{ background:"#003f7a", color:"#fff", fontWeight:600,
        padding:"4px 8px", fontSize:11, width:"18%", border:"1px solid #ddd" }}>
        {label}
      </td>
      <td style={{ background: alt ? "#dce8f5" : "#eef4fb", color:"#222",
        padding:"4px 8px", fontSize:11, width:"32%", border:"1px solid #ddd" }}>
        {value || ""}
      </td>
    </tr>
  );

  const TH = ({ children, align }) => (
    <th style={{ background:"#003f7a", color:"#fff", padding:"5px 8px",
      fontSize:10.5, textAlign: align || "left", border:"1px solid #ccc", fontWeight:600 }}>
      {children}
    </th>
  );

  const EarnRow = ({ name, amount, i }) => (
    <tr style={{ background: i%2===0 ? "#fff" : "#f5f9ff", color:"#222" }}>
      <td style={tdS}>{name}</td>
      <td style={{...tdS, textAlign:"right"}}>{fmtR(amount)}</td>
      <td style={{...tdS, textAlign:"right"}}>{fmtR(amount)}</td>
      <td style={{...tdS, textAlign:"right"}}>Rs. 0.0</td>
      <td style={{...tdS, textAlign:"right"}}>{fmtR(amount)}</td>
    </tr>
  );

  const DedRow = ({ name, amount, i }) => (
    <tr style={{ background: i%2===0 ? "#fff" : "#fff5f5", color:"#222" }}>
      <td style={tdS}>{name}</td>
      <td style={{...tdS, textAlign:"right"}}>Rs. 0.0</td>
      <td style={{...tdS, textAlign:"right"}}>{fmtR(amount)}</td>
      <td style={{...tdS, textAlign:"right"}}>Rs. 0.0</td>
      <td style={{...tdS, textAlign:"right"}}>{fmtR(amount)}</td>
    </tr>
  );

  const tdS = { padding:"4px 8px", fontSize:10.5, border:"1px solid #ddd", color:"#222" };

  const dedRows = [
    ...Object.entries(deductions),
    ...(ps.professional_tax > 0 && !Object.keys(deductions).some(k=>k.includes("Professional")) ? [["Professional Tax", ps.professional_tax]] : []),
    ...(ps.pf_employee > 0 && !Object.keys(deductions).some(k=>k.includes("PF")||k.includes("Provident")) ? [["Provident Fund", ps.pf_employee]] : []),
    ...(ps.esi_employee > 0 && !Object.keys(deductions).some(k=>k.includes("ESI")) ? [["ESI Employee", ps.esi_employee]] : []),
    // NOTE: individual loans (e.g. "Personal Loan", "Salary Advance") are
    // already present as their own named entries in components.deductions
    // — payroll.py inserts each one there directly. No fallback needed
    // (or safe to add) here: ps.loan_deduction is now a SUM across
    // possibly several differently-named loans, so re-adding it as one
    // generic "Loan Deduction" row would double-count against the named
    // entries already rendered above via Object.entries(deductions).
    ...(ps.tds > 0 ? [["Income Tax", ps.tds]] : []),
  ];

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:3000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      overflowY:"auto" }}>
      <div style={{ background:"#fff", borderRadius:8, width:"100%", maxWidth:820,
        boxShadow:"0 8px 40px rgba(0,0,0,0.3)", fontFamily:"Arial,sans-serif",
        overflow:"hidden", maxHeight:"95vh", overflowY:"auto" }}>

        {/* ── Action bar (follows app theme) ───────────────── */}
        <div style={{ background:T.header_bg, padding:"10px 18px",
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ color:T.text, fontWeight:700, fontSize:13,
            fontFamily:"Outfit,sans-serif" }}>
            Payslip — {ps.employee_name}
          </span>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => downloadPayslipPdf(ps.id, ps.employee_code, ps.month, setDownloading)}
              disabled={downloading}
              style={{ padding:"6px 14px", borderRadius:7, cursor:"pointer",
                border:`1px solid ${T.cyan}4D`, background:"none",
                color:T.cyan, fontFamily:"Outfit,sans-serif", fontSize:12,
                fontWeight:700, display:"flex", alignItems:"center", gap:5,
                opacity: downloading ? 0.6 : 1 }}>
              <Download size={12}/> {downloading ? "Downloading…" : "Download PDF"}
            </button>
            <button onClick={handleSend} disabled={sending || sent}
              style={{ padding:"6px 14px", borderRadius:7, cursor:"pointer",
                border:"none", background: sent ? T.green : `linear-gradient(135deg,${T.green},${T.cyan})`,
                color:"#fff", fontFamily:"Outfit,sans-serif",
                fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:5,
                opacity: sending ? 0.7 : 1 }}>
              <Send size={12}/> {sending ? "Sending…" : sent ? "Sent ✓" : "Send Email"}
            </button>
            <button onClick={onClose}
              style={{ background:T.toggle_bg, border:"none", borderRadius:"50%",
                width:28, height:28, cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", color:T.muted }}>
              <X size={13}/>
            </button>
          </div>
        </div>

        {/* ── Payslip document (white, matching PDF — fixed palette) ── */}
        <div style={{ padding:"20px 24px", background:"#fff" }}>

          {/* Company header */}
          <div style={{ display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              {companyLogo && (
                <img src={companyLogo} alt="logo"
                  style={{ height:90, maxWidth:240, maxHeight:90, objectFit:"contain", flexShrink:0 }}/>
              )}
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:"#003f7a" }}>
                  {companyName.toUpperCase()}
                </div>
                {companyAddr && (
                  <div style={{ fontSize:11, color:"#555", maxWidth:320, marginTop:3 }}>
                    {companyAddr}
                  </div>
                )}
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:11, color:"#666" }}>Payslip for</div>
              <div style={{ fontSize:15, fontWeight:800, color:"#003f7a" }}>{ps.month}</div>
            </div>
          </div>

          {/* Employee info grid */}
          <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12, fontSize:11 }}>
            <tbody>
              <InfoRow label="Employee Code" value={ps.employee_code} alt={true}/>
              <InfoRow label="Name"          value={ps.employee_name} alt={false}/>
              <InfoRow label="Department"    value={ps.department}    alt={true}/>
              <InfoRow label="Designation"   value={ps.designation}   alt={false}/>
            </tbody>
          </table>

          {/* Payroll days bar */}
          <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12 }}>
            <thead>
              <tr>
                {["Total Days","Days Paid","Arrear Days","Absent Days"].map(h => (
                  <th key={h} style={{ background:"#003f7a", color:"#fff",
                    padding:"5px 8px", fontSize:11, border:"1px solid #ccc", fontWeight:600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {[totalDays, daysPaid, 0, ps.lop_days||0].map((v,i) => (
                  <td key={i} style={{ textAlign:"center", padding:"4px",
                    fontSize:11, border:"1px solid #ddd", background:"#f5f9ff", color:"#222" }}>
                    {parseFloat(v).toFixed(1)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* Earnings */}
          <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:4 }}>
            <div style={{ width:6, height:22, background:"#00963f", borderRadius:"2px 0 0 2px" }}/>
            <div style={{ background:"#e8f7ee", color:"#00963f", fontWeight:700,
              fontSize:12, padding:"4px 12px", flex:1 }}>
              Earnings
            </div>
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12 }}>
            <thead>
              <tr>
                <TH>Earning Head</TH>
                <TH align="right">Rate</TH>
                <TH align="right">Earned Amount</TH>
                <TH align="right">Arrear Amount</TH>
                <TH align="right">Total Amount</TH>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background:"#dce8f5" }}>
                <td colSpan={5} style={{ padding:"4px 8px", fontSize:10.5,
                  fontWeight:700, color:"#003f7a", border:"1px solid #ddd" }}>
                  Fixed Earnings
                </td>
              </tr>
              {Object.entries(earnings).map(([name, amt], i) => (
                <EarnRow key={name} name={name} amount={amt} i={i}/>
              ))}
              {ps.lop_amount > 0 && (
                <tr style={{ background:"#fff5f5" }}>
                  <td style={tdS} colSpan={4}>LOP Deduction ({ps.lop_days} days)</td>
                  <td style={{...tdS, color:"#c80000", textAlign:"right"}}>
                    - {fmtR(ps.lop_amount)}
                  </td>
                </tr>
              )}
              {/* Gross total */}
              <tr style={{ background:"#dce8f5", fontWeight:700 }}>
                <td style={{ ...tdS, fontWeight:700 }}>Gross Earnings</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>{fmtR(ps.gross_salary)}</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>{fmtR(ps.gross_salary)}</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>Rs. 0.0</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>{fmtR(ps.gross_salary)}</td>
              </tr>
            </tbody>
          </table>

          {/* Deductions */}
          <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:4 }}>
            <div style={{ width:6, height:22, background:"#c8102e", borderRadius:"2px 0 0 2px" }}/>
            <div style={{ background:"#fde8ec", color:"#c8102e", fontWeight:700,
              fontSize:12, padding:"4px 12px", flex:1 }}>
              Deductions
            </div>
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12 }}>
            <thead>
              <tr>
                <TH>Deduction Head</TH>
                <TH align="right">CTC Rate</TH>
                <TH align="right">Amount</TH>
                <TH align="right">Arrear Amount</TH>
                <TH align="right">Total Amount</TH>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background:"#fde8ec" }}>
                <td colSpan={5} style={{ padding:"4px 8px", fontSize:10.5,
                  fontWeight:700, color:"#c8102e", border:"1px solid #ddd" }}>
                  Statutory Deductions
                </td>
              </tr>
              {dedRows.map(([name, amt], i) => (
                <DedRow key={name} name={name} amount={amt} i={i}/>
              ))}
              {/* Gross deductions total */}
              <tr style={{ background:"#dce8f5", fontWeight:700 }}>
                <td style={{ ...tdS, fontWeight:700 }}>Gross Deductions</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>Rs. 0.0</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>{fmtR(ps.total_deductions)}</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>Rs. 0.0</td>
                <td style={{ ...tdS, textAlign:"right", fontWeight:700 }}>{fmtR(ps.total_deductions)}</td>
              </tr>
            </tbody>
          </table>

          {/* Net salary */}
          <div style={{ borderTop:"2px solid #222", borderBottom:"2px solid #222",
            padding:"6px 8px", display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:8 }}>
            <span style={{ fontWeight:800, fontSize:13, color:"#222" }}>Net Salary</span>
            <span style={{ fontWeight:800, fontSize:14, color:"#003f7a" }}>
              Rs. {parseFloat(ps.net_salary||0).toLocaleString("en-IN", {minimumFractionDigits:1})}
            </span>
          </div>

          {/* Net in words */}
          <div style={{ fontSize:11, fontWeight:700, color:"#222", marginBottom:6 }}>
            Net Amount in words : {numToWords(ps.net_salary || 0)}
          </div>
          <div style={{ fontSize:10.5, color:"#444", fontStyle:"italic" }}>
            Note: This is a computer generated statement. Hence no signature is required.
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════
export default function Payslips() {
  const { T } = useTheme();
  const STATUS_COLOR = { Draft:T.muted, Final:T.cyan, Paid:T.green };
  const [payslips,   setPayslips]   = useState([]);
  const [runs,       setRuns]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [runFilter,  setRunFilter]  = useState("");
  const [monthFilter,setMonthFilter]= useState(() => new Date().toISOString().slice(0, 7)); // defaults to current month
  const [viewOpen,   setViewOpen]   = useState(false);
  const [sel,        setSel]        = useState(null);
  const [dlId,       setDlId]       = useState(null);
  const [sendId,     setSendId]     = useState(null);

  // ── Pagination ──────────────────────────────────────────────────
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  // Runs list is lightweight (one row per payroll run, not per employee)
  // — safe to fetch in full, once, for the "All Runs" dropdown.
  useEffect(() => {
    axios.get(APIConstant.payrollRuns, ax())
      .then(r => setRuns(r.data || []))
      .catch(() => setRuns([]));
  }, []);

  // Payslips are NOT fetched all-at-once anymore. At 700+/month and
  // 10,000+/year, loading everything up front was the actual bug —
  // this now always asks the backend for ONE month at a time
  // (GET /payslips/?month=YYYY-MM, already supported server-side),
  // re-fetching whenever the selected month changes. Search and the
  // run filter still work client-side, but only within that month's
  // already-small dataset (~700 rows), not the full history.
  const fetchPayslipsForMonth = async (month) => {
    setLoading(true);
    try {
      const m = month || new Date().toISOString().slice(0, 7);
      const r = await axios.get(`${APIConstant.payslips}?month=${m}`, ax());
      setPayslips(r.data || []);
    } catch (e) {
      console.error(e);
      setPayslips([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayslipsForMonth(monthFilter); }, [monthFilter]);

  // Search + run filter — month is no longer filtered here, since the
  // fetch itself is already scoped to one month.
  const filtered = useMemo(() => payslips.filter(p => {
    const matchS = !search    || `${p.employee_name} ${p.employee_code}`.toLowerCase().includes(search.toLowerCase());
    const matchR = !runFilter || String(p.run_id) === runFilter;
    return matchS && matchR;
  }), [payslips, search, runFilter]);

  // Reset to page 1 whenever search/month/run filter changes
  useEffect(() => { setPage(1); }, [search, runFilter, monthFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleSendRow = async (p) => {
    if (!window.confirm(`Send payslip email to ${p.employee_name}?`)) return;
    setSendId(p.id);
    try {
      await axios.post(`${API}/payslips/${p.id}/send-email`, {}, ax());
      alert(`✅ Payslip sent to ${p.employee_name}`);
    } catch(e) {
      alert("❌ " + (e.response?.data?.detail || "Send failed"));
    } finally { setSendId(null); }
  };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>COMPENSATION</div>
          <h2>Payslips</h2>
          <p>View, download and send employee payslips</p>
        </div>
      </div>

      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Viewing Month",  val:monthFilter,                                    color:"purple" },
          { label:"Total Payslips", val:payslips.length,                               color:"cyan"   },
          { label:"Paid",           val:payslips.filter(p=>p.status==="Paid").length,  color:"green"  },
          { label:"Draft",          val:payslips.filter(p=>p.status==="Draft").length, color:"amber"  },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.card}>
        {/* Filters — search (within month) + month (drives the fetch) + run */}
        <div style={{ display:"flex", gap:10, padding:"16px 22px",
          borderBottom:`1px solid ${T.border}`, flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:200 }}>
            <Search size={13} style={{ position:"absolute", left:10, top:"50%",
              transform:"translateY(-50%)", color:T.dim }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee…"
              style={{ width:"100%", background:T.inp_bg,
                border:`1px solid ${T.border}`, borderRadius:8,
                color:T.text, fontSize:13, padding:"7px 12px 7px 30px",
                outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
          </div>
          <input type="month" value={monthFilter}
            onChange={e => {
              // A blank/cleared month input would otherwise fall through
              // to fetching everything — always fall back to the current
              // month instead of allowing an unscoped fetch.
              const m = e.target.value || new Date().toISOString().slice(0, 7);
              setMonthFilter(m);
              setRunFilter(""); // clear run filter — it belonged to the previous month
            }}
            style={{ background:T.inp_bg,
              border:`1px solid ${T.border}`, borderRadius:8,
              color:T.text, fontSize:13, padding:"7px 12px",
              outline:"none", fontFamily:"Outfit,sans-serif" }}/>
          <select value={runFilter}
            onChange={e => {
              const newRunId = e.target.value;
              setRunFilter(newRunId);
              // A run belongs to exactly one month — jump the month filter
              // to match it (triggering a re-fetch) so picking a run from
              // the dropdown always shows that run's actual payslips,
              // instead of silently returning zero results because the
              // currently-loaded month doesn't contain it.
              if (newRunId) {
                const run = runs.find(r => String(r.id) === newRunId);
                if (run && run.month !== monthFilter) setMonthFilter(run.month);
              }
            }}
            style={{ background:T.inp_bg,
              border:`1px solid ${T.border}`, borderRadius:8,
              color:T.text, fontSize:13, padding:"7px 12px",
              outline:"none", fontFamily:"Outfit,sans-serif" }}>
            <option value="" style={{background:T.opt_bg}}>All Runs (this month)</option>
            {runs.map(r => <option key={r.id} value={r.id} style={{background:T.opt_bg}}>{r.month} — {r.status}</option>)}
          </select>
        </div>

        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr>
                  <th>Employee</th><th>Month</th><th>Basic</th>
                  <th>Gross</th><th>Deductions</th><th>Net Pay</th>
                  <th>LOP</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr className={styles.emptyRow}><td colSpan={9}>No payslips found</td></tr>
                ) : paginated.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight:500 }}>{p.employee_name}</div>
                      <div style={{ fontSize:11, color:T.muted }}>{p.employee_code} · {p.department}</div>
                    </td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan, fontSize:12 }}>{p.month}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{fmt(p.basic_salary)}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber }}>{fmt(p.gross_salary)}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.red }}>{fmt(p.total_deductions)}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.green, fontWeight:700 }}>{fmt(p.net_salary)}</span></td>
                    <td><span style={{ fontSize:12, color:p.lop_days>0?T.red:T.dim }}>
                      {p.lop_days > 0 ? `${p.lop_days}d` : "—"}
                    </span></td>
                    <td><span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600,
                      background:`${STATUS_COLOR[p.status]}20`, color:STATUS_COLOR[p.status] }}>
                      {p.status}
                    </span></td>
                    <td>
                      <div style={{ display:"flex", gap:4 }}>
                        {/* View */}
                        <button className={styles.btnSm}
                          onClick={() => { setSel(p); setViewOpen(true); }}
                          title="View payslip">
                          <FileText size={11}/> View
                        </button>
                        {/* Download PDF */}
                        <button className={styles.btnSm}
                          onClick={() => downloadPayslipPdf(p.id, p.employee_code, p.month,
                            v => setDlId(v ? p.id : null))}
                          disabled={dlId === p.id}
                          title="Download PDF">
                          <Download size={11}/> {dlId===p.id ? "…" : "PDF"}
                        </button>
                        {/* Send Email */}
                        <button className={styles.btnSm}
                          onClick={() => handleSendRow(p)}
                          disabled={sendId === p.id}
                          title="Send to employee email"
                          style={{ color:T.green }}>
                          <Send size={11}/> {sendId===p.id ? "…" : ""}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            T={T}
          />
        )}
      </div>

      {/* Payslip detail modal */}
      {viewOpen && sel && (
        <PayslipModal ps={sel} onClose={() => { setViewOpen(false); setSel(null); }}/>
      )}
    </div>
  );
}